import { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import useData from "./useData";
import usePage from "./usePage";
import { getId } from "../utils/idHelpers";

const useSurveyMetrics = () => {
  const { executeAction, getFromServer } = useData();
  const { showMessage } = usePage();

  const toOption = (item, fallbackName = "") => {
    const id = getId(item);
    const name =
      item?.name ||
      item?.title ||
      item?.label ||
      item?.legalName ||
      fallbackName;

    if (!id || !name) return null;

    return {
      ...item,
      id: String(id),
      name,
    };
  };

  // Filter state
  const [filters, setFilters] = useState({
    clientIds: [],
    projectIds: [],
    surveyIds: [],
    startDate: null,
    endDate: null,
  });

  // Dropdown data
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);

  // Survey metrics data
  const [metricsData, setMetricsData] = useState({
    data: null,
    isLoading: false,
    error: null,
  });

  // Initialize filters with current month on mount
  useEffect(() => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    setFilters((prev) => ({
      ...prev,
      startDate: firstDay,
      endDate: lastDay,
    }));

    // Load clients on mount
    loadClients();
  }, []);

  // Update filters with debounce to avoid excessive API calls
  const updateFilters = useCallback(
    debounce((newFilters) => {
      setFilters((prev) => ({ ...prev, ...newFilters }));
    }, 300),
    [],
  );

  // ===== LOAD CLIENTS =====
  const loadClients = useCallback(async () => {
    setLoadingDropdowns(true);
    try {
      const results = await executeAction({
        entityName: "Client",
        action: "get",
        filter: { isActive: true },
      });

      if (results?.isSuccessful && Array.isArray(results?.data)) {
        const normalized = results.data
          .map((client) => toOption(client))
          .filter(Boolean);
        setClients(normalized);
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      setClients([]);
    } finally {
      setLoadingDropdowns(false);
    }
  }, [executeAction]);

  // ===== LOAD PROJECTS =====
  const loadProjects = useCallback(
    async (clientIds) => {
      if (!clientIds || clientIds.length === 0) {
        setProjects([]);
        return;
      }

      setLoadingDropdowns(true);
      try {
        const results = await executeAction({
          entityName: "Project",
          action: "get",
          filter: {
            isActive: true,
            clientId: { $in: clientIds },
          },
        });

        if (results?.isSuccessful && Array.isArray(results?.data)) {
          const normalized = results.data
            .map((project) => toOption(project))
            .filter(Boolean);
          setProjects(normalized);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error("Error loading projects:", error);
        setProjects([]);
      } finally {
        setLoadingDropdowns(false);
      }
    },
    [executeAction],
  );

  // ===== LOAD SURVEYS =====
  const loadSurveys = useCallback(
    async (projectIds) => {
      if (!projectIds || projectIds.length === 0) {
        setSurveys([]);
        return;
      }

      setLoadingDropdowns(true);
      try {
        const results = await executeAction({
          entityName: "Survey",
          action: "get",
          filter: {
            isActive: true,
            projectId: { $in: projectIds },
          },
        });

        if (results?.isSuccessful && Array.isArray(results?.data)) {
          const normalized = results.data
            .map((survey) => toOption(survey))
            .filter(Boolean);
          setSurveys(normalized);
        } else {
          setSurveys([]);
        }
      } catch (error) {
        console.error("Error loading surveys:", error);
        setSurveys([]);
      } finally {
        setLoadingDropdowns(false);
      }
    },
    [executeAction],
  );

  // ===== UPDATE CLIENT FILTER =====
  const updateClientFilter = useCallback(
    (clientIds) => {
      updateFilters({ clientIds, projectIds: [], surveyIds: [] });

      // Load projects for selected clients
      if (clientIds && clientIds.length > 0) {
        loadProjects(clientIds);
      } else {
        setProjects([]);
        setSurveys([]);
      }
    },
    [updateFilters, loadProjects],
  );

  // ===== UPDATE PROJECT FILTER =====
  const updateProjectFilter = useCallback(
    (projectIds) => {
      updateFilters({ projectIds, surveyIds: [] });

      // Load surveys for selected projects
      if (projectIds && projectIds.length > 0) {
        loadSurveys(projectIds);
      } else {
        setSurveys([]);
      }
    },
    [updateFilters, loadSurveys],
  );

  // ===== UPDATE SURVEY FILTER =====
  const updateSurveyFilter = useCallback(
    (surveyIds) => {
      updateFilters({ surveyIds });
    },
    [updateFilters],
  );

  // ===== UPDATE DATE RANGE =====
  const updateDateRange = useCallback(
    (startDate, endDate) => {
      updateFilters({ startDate, endDate });
    },
    [updateFilters],
  );

  // ===== GET SURVEY METRICS OVERVIEW =====
  const getSurveyMetrics = useCallback(
    async (customFilters = null) => {
      setMetricsData((prev) => ({ ...prev, isLoading: true, error: null }));
      try {
        const filterParams = customFilters || filters;

        const result = await executeAction({
          entityName: "SurveyMetrics",
          action: "surveyMetricsOverview",
          data: {
            clientIds: filterParams.clientIds || [],
            projectIds: filterParams.projectIds || [],
            surveyIds: filterParams.surveyIds || [],
            startDate: filterParams.startDate,
            endDate: filterParams.endDate,
          },
        });

        if (result?.isSuccessful && result?.data) {
          setMetricsData((prev) => ({
            ...prev,
            data: result.data,
            isLoading: false,
          }));
        } else {
          setMetricsData((prev) => ({
            ...prev,
            error: result?.message || "Failed to fetch survey metrics",
            isLoading: false,
          }));
        }
      } catch (error) {
        setMetricsData((prev) => ({
          ...prev,
          error: error.message || "Error fetching survey metrics",
          isLoading: false,
        }));
      }
    },
    [filters, executeAction],
  );

  // Auto-fetch metrics when filters change (but only if dates are set)
  useEffect(() => {
    if (filters.startDate && filters.endDate) {
      getSurveyMetrics();
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.clientIds,
    filters.projectIds,
    filters.surveyIds,
  ]);

  return {
    // Filter state
    filters,
    updateClientFilter,
    updateProjectFilter,
    updateSurveyFilter,
    updateDateRange,

    // Dropdown data
    clients,
    projects,
    surveys,
    loadingDropdowns,

    // Metrics data
    metricsData,
    getSurveyMetrics,
  };
};

export default useSurveyMetrics;
